import { ReactNode, isValidElement, cloneElement } from 'react';
import { useKeyboardContext } from '@/context/KeyboardContext';

// Same subtle purple gradient colors used by the loader for seamless transition
const SHELL_COLORS = {
  bgLight: '#fcf4fc', // primaryWhisper
  bgMid: '#f8e8f8', // primaryGhost
  bgDark: '#f0d2f0', // primaryFaint
} as const;

interface AppShellProps {
  /** Header component - rendered at top with fixed height */
  header?: ReactNode;
  /** Navigation component (BottomNavigation) - rendered at bottom */
  navigation?: ReactNode;
  /** Main content - fills remaining space and scrolls independently */
  children: ReactNode;
  /** Additional className for the shell container */
  className?: string;
  /** Additional className for the content area */
  contentClassName?: string;
}

/**
 * AppShell - Modern mobile-first layout container
 *
 * Provides a fixed viewport layout with:
 * - Fixed header at top (uses --header-height CSS variable)
 * - Navigation at bottom (uses --nav-total-height CSS variable)
 * - Scrollable content area that fills remaining space
 * - Safe area support for notched devices (handled by BottomNavigation)
 * - Prevents iOS bounce scroll issues
 *
 * The navigation is always rendered (never conditionally removed from the DOM).
 * Keyboard visibility is passed as a `hidden` prop to the navigation component,
 * which uses CSS transforms to slide off-screen. This avoids layout reflow and
 * the Safari stacking-context bugs caused by conditional rendering + display:none.
 *
 * Layout structure:
 * ┌─────────────────────────────────────┐
 * │  Safe area top (cover)              │
 * ├─────────────────────────────────────┤
 * │  Header (fixed height)              │
 * ├─────────────────────────────────────┤
 * │                                     │
 * │  Content (flex-1, scrollable)       │
 * │                                     │
 * ├─────────────────────────────────────┤
 * │  Navigation (--nav-height + safe)   │
 * └─────────────────────────────────────┘
 */
export function AppShell({
  header,
  navigation,
  children,
  className = '',
  contentClassName = '',
}: AppShellProps) {
  const { isKeyboardVisible } = useKeyboardContext();

  // Clone the navigation element to inject the `hidden` prop based on keyboard state.
  // This lets BottomNavigation handle its own show/hide via CSS transforms.
  const navigationElement =
    navigation && isValidElement<{ hidden?: boolean }>(navigation)
      ? cloneElement(navigation, { hidden: isKeyboardVisible })
      : navigation;

  return (
    <div
      className={`
        fixed inset-0 
        flex flex-col 
        overflow-hidden
        ${className}
      `}
      style={{
        height: 'var(--app-height)',
        paddingTop: 'var(--safe-area-top)',
        // Subtle purple gradient matching the loader for seamless transition
        background: `linear-gradient(135deg, ${SHELL_COLORS.bgLight} 0%, ${SHELL_COLORS.bgMid} 50%, ${SHELL_COLORS.bgDark} 100%)`,
      }}
    >
      {/* Top safe area cover - white bar for Safari PWA status bar area */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: 0,
          height: 'env(safe-area-inset-top, 0px)',
          background: '#ffffff',
          zIndex: 49,
        }}
      />

      {/* Header - fixed height, doesn't scroll */}
      {header && (
        <div className="flex-shrink-0 z-40" style={{ height: 'var(--header-height)' }}>
          {header}
        </div>
      )}

      {/* Main content area - fills remaining space, children control their own scrolling */}
      <main
        className={`
          flex-1 
          min-h-0
          overflow-hidden
          ${contentClassName}
        `}
      >
        {/* Centered container for content - uses h-full so children can use flex */}
        <div className="max-w-md mx-auto h-full bg-white shadow-2xl shadow-[#e5bce5]/30">
          {children}
        </div>
      </main>

      {/*
       * Navigation slot - always rendered, never conditionally removed.
       * BottomNavigation handles its own:
       *   - Safe area padding (additive, below nav content)
       *   - Show/hide via CSS transform (passed via `hidden` prop)
       *   - Background that extends through the safe area
       *
       * The wrapper uses overflow-hidden to clip the nav during its
       * translateY slide-out, preventing it from overlapping content below.
       */}
      {navigation && (
        <div
          data-navigation-wrapper
          data-keyboard-visible={isKeyboardVisible || undefined}
          className="flex-shrink-0 z-50 overflow-hidden"
          style={{
            /*
             * Use --nav-total-height (nav + safe area) so the flex layout
             * always reserves the correct amount of space. When the keyboard
             * is visible, collapse to 0 so content fills the space.
             */
            height: isKeyboardVisible ? '0px' : 'var(--nav-total-height)',
            transition: 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {navigationElement}
        </div>
      )}
    </div>
  );
}

/**
 * ContentSection - Wrapper for page content within AppShell
 *
 * Use this to wrap page sections that need to fill the available
 * height within the content area.
 */
export function ContentSection({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`flex flex-col h-full ${className}`}>{children}</div>;
}

export default AppShell;
